import React, { useCallback, useEffect, useRef, useState } from 'react';
import useAnalyticsTracker from '../../../../../analytics/hooks/useAnalyticsTracker';
import {
  TRACKER_ADD_MANAGED_WAREHOUSE_CLICK,
  TRACKER_DESTINATION_TYPE_EMPTY_RESULT,
  TRACKER_MANAGED_WAREHOUSE_CARD_CLICK,
  TRACKER_SELECT_DEST_TYPE,
  TRACKER_SHOW_ALL_DESTINATION_TYPES_CLICK,
  TRACKER_SUGGEST_DESTINATION_CLICK
} from '../../../../../connector/connector-form/tracking/connectorFormTracking';
import { FEATURE_REQUEST_HANDLER } from '../../../../../core/constants/feedback';
import { DestinationMode } from '../../../../../core/interfaces/destination-mode';
import { HevoEntity, HevoEntityItem } from '../../../../../core/interfaces/hevo-entity';
import { getSortedListFromReferenceList } from '../../../../../core/utils/array';
import { getDataIdGenerator } from '../../../../../core/utils/generateDataId';
import { matchPattern } from '../../../../../core/utils/string';
import { DestinationAPI } from '../../../../../edge/destinations/api/DestinationAPI';
import { EdgeDestinationTypeTilesFactory } from '../../../../../edge/destinations/create-destination/factories/destinationTilesFactory';
import { RawDestinationType as EdgeRawDestinationType } from '../../../../../edge/destinations/interfaces/destination';
import { NoEntityTypeAlert } from '../../../../../fallback-screens/components/no-entity-type-alert/NoEntityTypeAlert';
import RetryApiAlert from '../../../../../fallback-screens/components/retry-api-alert/RetryApiAlert';
import {
  TileGrid,
  TileSkeletonGrid,
  useResponsiveColumnCount
} from '@hevoio/hevo-ui-toolkit';
import {
  DEFAULT_TILE_HEIGHT,
  getTileGridWidth
} from '../../../../../layout/constants/tileGrid';
import EntityTile from '../../../../../layout/components/entity-tile/EntityTile';
import { EntityTileProps } from '../../../../../layout/interfaces/tile';
import SearchArea from '../../../../../list/components/search-area/SearchArea';
import StickySearchWrapper from '../../../../../list/components/sticky-search-wrapper/StickySearchWrapper';
import useEmptySearchResultTrackerService from '../../../../../list/hooks/useEmptySearchResultTrackerService';
import SuggestByType from '../../../../../support/components/request-feature-by-type/SuggestByType';
import useTeamSettingsService from '../../../../team/hooks/useTeamSettingsService';
import StandardDestinationsAPI from '../../../api/DestinationsAPI';
import { DESTINATION_TYPE_ORDER_BY_USAGE } from '../../../constants/destinationTypes';
import { DestinationVersions } from '../../../interfaces/destination';
import {
  MANAGED_WAREHOUSE_TILE_CONFIG,
  MANAGED_WAREHOUSE_TILE_IDENTIFIER,
  VIEW_ALL_TILE_CONFIG,
  VIEW_ALL_TILE_IDENTIFIER
} from '../../constants/selectDestinationTypeList';
import { MergedDestinationTypeTilesFactory } from '../../factories/mergedDestinationTypeTilesFactory';
import StandardDestinationTypeTilesFactory from '../../factories/standardDestinationTypeTilesFactory';
import { mergeDestinationTypes } from '../../utils/mergeDestinationTypes';
import AddManagedWarehouseBanner from '../add-managed-warehouse-banner/AddManagedWarehouseBanner';
import BigQueryManageWarehouse from '../big-query-manged-warehouse-card/BigQueryManageWarehouseCard';
import styles from './DestinationTypeList.module.scss';

const DEBOUNCE_INTERVAL = 500;

export const dataId = getDataIdGenerator('destinationTypeList')('');

export interface ManagedWarehouseInterface {
  canResolveManagedWarehouse: boolean;
  sourceType: string;
  setupWarehouse: Function;
}

interface DestinationTypeListProps {
  onTypeSelect: Function;
  hevoEntityType: HevoEntityItem;
  destinationKeyword: string;
  destinationMode: DestinationMode;
  saveSearch: Function;
  defaultSearch?: string;
  filterMethod?: Function;
  managedWarehouse: ManagedWarehouseInterface;
  showManageWarehouseCard?: boolean;
  disableSelection?: (destinationType: any) => boolean;
  versions?: DestinationVersions[];
  commonSelection?: boolean;
  scrollElement?: HTMLElement | null;
}

function DestinationTypeList({
  onTypeSelect,
  hevoEntityType,
  destinationKeyword,
  destinationMode,
  saveSearch,
  defaultSearch,
  filterMethod,
  managedWarehouse,
  showManageWarehouseCard,
  disableSelection,
  versions = [DestinationVersions.STANDARD],
  commonSelection,
  scrollElement
}: DestinationTypeListProps) {
  const [allDestinations, setAllDestinations] = useState([]);
  const [filteredDestinations, setFilteredDestinations] = useState([]);
  const [currentDestList, setCurrentDestList] = useState([]);

  const [searchTerm, setSearchTerm] = useState(defaultSearch);
  const [showManagedWarehouseBanner, setShowManagedWarehouseBanner] = useState(false);
  const [showPreferredDestinationsView, setShowPreferredDestinationsView] = useState(false);

  const [apiError, setApiError] = useState(null);
  const [loading, setLoading] = useState(true);
  const canSetupWarehouse = useRef(false);

  const analyticsTracker = useAnalyticsTracker();
  const emptyResultTracker = useEmptySearchResultTrackerService();
  const teamSettingsService = useTeamSettingsService();

  // Same hook TileGrid and TileSkeletonGrid call, so the reserved width can never
  // disagree with the column count they actually render.
  const tileColumnCount = useResponsiveColumnCount();
  const tilesColumnStyle = {
    '--dest-tiles-width': `${getTileGridWidth(tileColumnCount)}px`
  } as React.CSSProperties;

  const preferredDestinationIdentifiers =
    teamSettingsService.getTeamSettings()?.preferred_destinations || [];

  const showDestinationTypes = allDestinations?.length;
  const showPageError = !!apiError;
  const showSuggestByType = filteredDestinations.length > 0 && allDestinations?.length;

  const showNoMatchAlert = filteredDestinations.length === 0 && !loading && !apiError;

  const suggestionContext = { name: destinationKeyword, value: destinationMode };

  useEffect(() => {
    getPageData();
  }, []);

  useEffect(() => {
    if (showManagedWarehouseBanner === true) {
      setFilteredDestinations(allDestinations);
    }
  }, [showManagedWarehouseBanner, allDestinations]);

  const showPreferredDestinationsFirst = hevoEntityType.value === HevoEntity.PIPELINE.value;

  const getPageData = () => {
    getDestinationTypes();
  };

  const getDestinationTypes = async () => {
    if (apiError) {
      setApiError(null);
    }

    setLoading(true);

    try {
      let [
        // tslint:disable-next-line:prefer-const
        showManagedWarehouse,
        rawEdgeDestinationTypes,
        rawStandardDestinationTypes
      ] = await Promise.all([
        isManagedWarehouseApplicable(),
        versions.includes(DestinationVersions.EDGE)
          ? DestinationAPI.getDestinationTypes().then(res => res.data)
          : Promise.resolve<EdgeRawDestinationType[]>([]),
        versions.includes(DestinationVersions.STANDARD)
          ? StandardDestinationsAPI.getDestinationTypes(false).then(res => res.data)
          : Promise.resolve([])
      ]);

      canSetupWarehouse.current = showManagedWarehouse;

      if (typeof filterMethod === 'function') {
        rawEdgeDestinationTypes = filterMethod(rawEdgeDestinationTypes);
        rawStandardDestinationTypes = filterMethod(rawStandardDestinationTypes);
      }

      let destinationsTilesData = commonSelection
        ? MergedDestinationTypeTilesFactory(
          mergeDestinationTypes(
            rawStandardDestinationTypes,
            rawEdgeDestinationTypes
          ), disableSelection
        ) : [
          ...EdgeDestinationTypeTilesFactory(rawEdgeDestinationTypes),
          ...StandardDestinationTypeTilesFactory(rawStandardDestinationTypes)
        ];

      destinationsTilesData = getSortedListFromReferenceList(
        destinationsTilesData,
        DESTINATION_TYPE_ORDER_BY_USAGE,
        item => item.id
      );

      _processDestinationTypes(destinationsTilesData, showManagedWarehouse);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      setApiError(error);
    }
  };

  const _processDestinationTypes = (
    destinationTiles: Array<EntityTileProps>,
    showManagedWarehouse = false
  ) => {
    let updatedDestinationTiles;

    if (showPreferredDestinationsFirst) {
      let preferredDestinations;

      const showManagedWarehouseForExperiment =
        showManagedWarehouse &&
        (!preferredDestinationIdentifiers.length ||
          !!preferredDestinationIdentifiers.find(
            destinationIdentifier => destinationIdentifier === MANAGED_WAREHOUSE_TILE_IDENTIFIER
          ));

      if (showManagedWarehouseForExperiment) {
        preferredDestinations = [MANAGED_WAREHOUSE_TILE_CONFIG];

        // Adding managed destination to all destination as it needs to be included in search
        updatedDestinationTiles = [...preferredDestinations, ...destinationTiles];
      } else {
        preferredDestinations = destinationTiles.filter(destination =>
          preferredDestinationIdentifiers.includes(destination.id)
        );

        updatedDestinationTiles = [
          ...preferredDestinations,
          ...destinationTiles.filter(
            destination => !preferredDestinationIdentifiers.includes(destination.id)
          )
        ];
      }

      if (preferredDestinations.length) {
        if (preferredDestinations.length < destinationTiles.length) {
          preferredDestinations.push(VIEW_ALL_TILE_CONFIG);
        }

        preferredDestinations = getListSortedByIndex(preferredDestinations);

        setCurrentDestList(preferredDestinations);
        setShowPreferredDestinationsView(true);
      } else {
        setCurrentDestList(destinationTiles);
      }
    } else {
      updatedDestinationTiles = destinationTiles;

      if (showManagedWarehouse) {
        setShowManagedWarehouseBanner(true);
      }
    }

    setAllDestinations(getListSortedByIndex(updatedDestinationTiles));
  };

  const isManagedWarehouseApplicable = async () => {
    if (!managedWarehouse.canResolveManagedWarehouse) {
      return false;
    }

    const { data } = await StandardDestinationsAPI.getSourceTypeMetadata(
      managedWarehouse.sourceType
    );

    return !!data?.mbq_warehouse_setup;
  };

  const getListSortedByIndex = list =>
    list.map((item, index) => ({
      ...item,
      sortOrder: index
    }));

  const onSearch = useCallback(
    (searchInput: string) => {
      /**
       * calling this function and passing the search string will save the string in state machine
       */
      saveSearch(searchInput);
      setSearchTerm(searchInput);

      if (searchInput.trim().length === 0) {
        setFilteredDestinations(showPreferredDestinationsView ? currentDestList : allDestinations);
        return;
      }

      const searchedDestinations = allDestinations.filter(destination =>
        matchPattern(
          [destination.data.type, destination.data.destinationType, destination.data.dest_type_display, ...(destination.data.search_keywords || [])],
          searchInput
        )
      );

      setFilteredDestinations(searchedDestinations);

      if (searchedDestinations.length === 0) {
        emptyResultTracker.onEmptyResult(TRACKER_DESTINATION_TYPE_EMPTY_RESULT, searchInput, {
          ...getSearchAnalyticsData()
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allDestinations, showPreferredDestinationsView, currentDestList]
  );

  const getSearchAnalyticsData = () => ({
    searchTerm,
    searchResultEmpty: filteredDestinations.length === 0,
    context: hevoEntityType.toString(),
    preferredDestinationsView: showPreferredDestinationsView
  });

  const onSuggestDestinationClick = () => {
    analyticsTracker.eventTrack({
      action: TRACKER_SUGGEST_DESTINATION_CLICK,
      properties: {
        entityType: hevoEntityType.toString(),
        handler: FEATURE_REQUEST_HANDLER,
        ...getSearchAnalyticsData()
      }
    });
  };

  const onCreateManagedWarehouseClick = () => {
    analyticsTracker.eventTrack({
      action: TRACKER_ADD_MANAGED_WAREHOUSE_CLICK,
      properties: getSearchAnalyticsData()
    });

    managedWarehouse.setupWarehouse();
  };

  const onManagedWarehouseCardClick = () => {
    analyticsTracker.eventTrack({
      action: TRACKER_MANAGED_WAREHOUSE_CARD_CLICK,
      properties: getSearchAnalyticsData()
    });

    managedWarehouse.setupWarehouse();
  };

  const onShowAllDestinationTypesClick = () => {
    analyticsTracker.eventTrack({
      action: TRACKER_SHOW_ALL_DESTINATION_TYPES_CLICK,
      properties: getSearchAnalyticsData()
    });

    setShowPreferredDestinationsView(false);
  };

  const onDestinationTypeClick = (destinationType: any) => {
    if (destinationType.dest_type === VIEW_ALL_TILE_IDENTIFIER) {
      onShowAllDestinationTypesClick();
      return;
    }

    if (destinationType.dest_type === MANAGED_WAREHOUSE_TILE_IDENTIFIER) {
      onManagedWarehouseCardClick();
      return;
    }

    const isPreferredDestination = preferredDestinationIdentifiers.includes(
      destinationType.dest_type
    );

    analyticsTracker.eventTrack({
      action: TRACKER_SELECT_DEST_TYPE,
      properties: {
        destType: destinationType.dest_type,
        searchTerm,
        preferred: isPreferredDestination,
        context: hevoEntityType.toString(),
        preferredDestinationsView: showPreferredDestinationsView,
        visibleVersions: versions.join(',')
      }
    });

    onTypeSelect(destinationType);
  };

  return (
    <>
      {showManagedWarehouseBanner ? (
        <AddManagedWarehouseBanner
          type={destinationKeyword}
          actionHandler={onCreateManagedWarehouseClick}
        />
      ) : (
        <StickySearchWrapper>
          <SearchArea
            dataId={dataId}
            autofocus
            onSearch={onSearch}
            debounceInterval={DEBOUNCE_INTERVAL}
            placeholder={`Search ${destinationKeyword} Types`}
            collapsible={false}
            defaultSearch={defaultSearch}
          />
        </StickySearchWrapper>
      )}

      <div className={styles.typeListWrapper} style={tilesColumnStyle}>
        <div className={styles.tilesColumn}>
          {showDestinationTypes ? (
            <TileGrid
              tileHeight={DEFAULT_TILE_HEIGHT}
              tiles={filteredDestinations}
              onTileClick={onDestinationTypeClick}
              TileComponent={EntityTile}
              scrollElement={scrollElement}
            />
          ) : null}

          {showNoMatchAlert ? (
            <NoEntityTypeAlert
              suggestionContext={suggestionContext}
              actionHandler={onSuggestDestinationClick}
            />
          ) : null}

          {loading ? <TileSkeletonGrid tileCount={tileColumnCount * 2} /> : null}

          {showPageError ? (
            <RetryApiAlert actionHandler={getPageData} error={apiError} dataId={dataId} />
          ) : null}

          {showManageWarehouseCard && canSetupWarehouse.current ? (
            <BigQueryManageWarehouse actionHandler={onCreateManagedWarehouseClick} />
          ) : null}
        </div>
      </div>

      {showSuggestByType ? (
        <SuggestByType
          suggestionContext={suggestionContext}
          actionHandler={onSuggestDestinationClick}
        />
      ) : null}
    </>
  );
}

export default DestinationTypeList;
